import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { HeartPulse, AlertTriangle, CheckCircle2, Clock, BellOff, RotateCcw, Send, X } from 'lucide-react';

type HealthStatus = 'healthy' | 'degraded' | 'broken' | 'paused' | 'disconnected' | 'unknown';
type TriageState = 'open' | 'acknowledged' | 'snoozed' | 'resolved';
type Severity = 'critical' | 'warning' | 'info' | null;

interface HealthRow {
  id: string;
  storeId: number;
  storeName: string | null;
  platform: string;
  status: HealthStatus;
  reasonCode: string | null;
  severity: Severity;
  consecutiveFailures: number;
  brokenSince: string | null;
  lastHealthyAt: string | null;
  lastCheckedAt: string | null;
  triageState: TriageState;
  snoozedUntil: string | null;
  triageNote: string | null;
  notifiedAt: string | null;
}

interface NotifyResult {
  reconnectUrl: string;
  recipients: number;
  results: Array<{ channel: string; recipient: string | null; status: string }>;
}

const ADMIN_BASE = '/api/v1/engagement/admin/connection-health';

const STATUS_BADGE: Record<HealthStatus, string> = {
  broken: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300',
  degraded: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  healthy: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300',
  paused: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  disconnected: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  unknown: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

const TRIAGE_BADGE: Record<TriageState, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  acknowledged: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300',
  snoozed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300',
};

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function ConnectionHealthPage() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'broken' | 'degraded'>('all');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [includeSnoozed, setIncludeSnoozed] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const params = new URLSearchParams();
  if (statusFilter !== 'all') params.set('status', statusFilter);
  if (includeResolved) params.set('includeResolved', 'true');
  if (includeSnoozed) params.set('includeSnoozed', 'true');

  const queueQuery = useQuery({
    queryKey: ['connection-health', statusFilter, includeResolved, includeSnoozed],
    queryFn: async ({ signal }) => {
      const res = await api.get<HealthRow[]>(`${ADMIN_BASE}?${params.toString()}`, { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load');
      return res.data;
    },
    refetchInterval: 60_000,
  });

  const action = useMutation({
    mutationFn: async ({ id, verb, body }: { id: string; verb: string; body?: unknown }) => {
      const res = await api.post(`${ADMIN_BASE}/${id}/${verb}`, body ?? {});
      if (!res.success) throw new Error(res.error ?? `${verb} failed`);
      return res;
    },
    onSuccess: (_data, vars) => {
      setToast({ message: `Connection ${vars.verb}d.`, type: 'success' });
      void queueQuery.refetch();
    },
    onError: (err) => {
      setToast({ message: err instanceof Error ? err.message : 'Action failed', type: 'error' });
    },
  });

  const notify = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<NotifyResult>(`${ADMIN_BASE}/${id}/notify`, {});
      if (!res.success || !res.data) throw new Error(res.error ?? 'Notify failed');
      return res.data;
    },
    onSuccess: (data) => {
      setToast({
        message:
          data.recipients > 0
            ? `Reconnect link sent to ${data.recipients} recipient(s) + in-app.`
            : 'Reconnect link created and posted in-app (no verified email recipients).',
        type: 'success',
      });
      void queueQuery.refetch();
    },
    onError: (err) => {
      setToast({ message: err instanceof Error ? err.message : 'Notify failed', type: 'error' });
    },
  });

  const rows = queueQuery.data ?? [];
  const busy = action.isPending || notify.isPending;

  const onNotify = (r: HealthRow) => {
    const who = r.storeName ?? `store #${r.storeId}`;
    if (window.confirm(`Send a reconnect notification (email + in-app) to ${who}'s owners for ${r.platform.replace(/_/g, ' ')}?`)) {
      notify.mutate(r.id);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <HeartPulse className="w-6 h-6 text-red-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Connection Health</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Triage queue for degraded and broken platform connections across all stores.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'broken', 'degraded'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setStatusFilter(f)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
              statusFilter === f
                ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {f}
          </button>
        ))}
        <label className="ml-2 flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={includeSnoozed} onChange={(e) => setIncludeSnoozed(e.target.checked)} />
          Show snoozed
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={includeResolved} onChange={(e) => setIncludeResolved(e.target.checked)} />
          Show resolved
        </label>
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">{rows.length} connection(s)</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-left text-xs uppercase text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2.5 font-medium">Store</th>
              <th className="px-4 py-2.5 font-medium">Platform</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Reason</th>
              <th className="px-4 py-2.5 font-medium">Broken</th>
              <th className="px-4 py-2.5 font-medium">Fails</th>
              <th className="px-4 py-2.5 font-medium">Triage</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {queueQuery.isLoading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            )}
            {!queueQuery.isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-green-500" />
                No connections need attention.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="text-gray-700 dark:text-gray-200">
                <td className="px-4 py-2.5">
                  <div className="font-medium">{r.storeName ?? `Store #${r.storeId}`}</div>
                  <div className="text-xs text-gray-400">#{r.storeId}</div>
                </td>
                <td className="px-4 py-2.5 capitalize">{r.platform.replace(/_/g, ' ')}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[r.status]}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs">{r.reasonCode ?? '—'}</td>
                <td className="px-4 py-2.5 text-xs whitespace-nowrap">{relativeTime(r.brokenSince)}</td>
                <td className="px-4 py-2.5">{r.consecutiveFailures}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${TRIAGE_BADGE[r.triageState]}`}>
                    {r.triageState}
                  </span>
                  {r.triageState === 'snoozed' && r.snoozedUntil && (
                    <div className="mt-0.5 text-[11px] text-gray-400">until {new Date(r.snoozedUntil).toLocaleString()}</div>
                  )}
                  {r.notifiedAt && (
                    <div className="mt-0.5 text-[11px] text-indigo-500">notified {relativeTime(r.notifiedAt)}</div>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    {r.triageState !== 'resolved' && (
                      <ActionBtn disabled={busy} onClick={() => onNotify(r)} title="Notify client (send reconnect link)">
                        <Send className="w-4 h-4 text-blue-600" />
                      </ActionBtn>
                    )}
                    {r.triageState === 'open' && (
                      <ActionBtn disabled={busy} onClick={() => action.mutate({ id: r.id, verb: 'acknowledge' })} title="Acknowledge">
                        <CheckCircle2 className="w-4 h-4" />
                      </ActionBtn>
                    )}
                    {r.triageState !== 'snoozed' && r.triageState !== 'resolved' && (
                      <ActionBtn disabled={busy} onClick={() => action.mutate({ id: r.id, verb: 'snooze', body: { hours: 24 } })} title="Snooze 24h">
                        <Clock className="w-4 h-4" />
                      </ActionBtn>
                    )}
                    {r.triageState !== 'resolved' && (
                      <ActionBtn disabled={busy} onClick={() => action.mutate({ id: r.id, verb: 'resolve' })} title="Resolve">
                        <BellOff className="w-4 h-4" />
                      </ActionBtn>
                    )}
                    {(r.triageState === 'resolved' || r.triageState === 'snoozed') && (
                      <ActionBtn disabled={busy} onClick={() => action.mutate({ id: r.id, verb: 'reopen' })} title="Reopen">
                        <RotateCcw className="w-4 h-4" />
                      </ActionBtn>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {queueQuery.isError && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="w-4 h-4" /> Failed to load the triage queue.
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-5 right-5 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm text-white shadow-lg ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
          <button type="button" onClick={() => setToast(null)}><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

function ActionBtn({
  children,
  onClick,
  title,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
    >
      {children}
    </button>
  );
}
